import React from 'react';
import { Helmet } from 'react-helmet-async';
import { PAGE_BY_PATH, fullTitleOf } from './pages';
import { TITLE_SUFFIX, LOCALE, LANG, SITE_NAME, DEFAULT_OG_IMAGE, absoluteUrl } from './site';
import { breadcrumbSchema, faqSchema } from './schema';

/**
 * Page metadata for every routed page. Title and description come from the
 * registry in pages.js unless a page overrides them; canonical, robots, Open
 * Graph, Twitter and JSON-LD are derived here, once.
 *
 *   <Seo path="/point-of-sale" faqs={FAQS} schema={[softwareSchema(...)]} />
 */
const Seo = ({ path, title, description, image, noindex = false, faqs, schema = [] }) => {
  const page = PAGE_BY_PATH[path];
  if (!page) throw new Error(`<Seo>: no entry for "${path}" in src/seo/pages.js`);

  const fullTitle = title ? `${title}${TITLE_SUFFIX}` : fullTitleOf(page);
  const desc = description || page.description;
  const indexable = page.index && !noindex;
  const url = absoluteUrl(path === '*' ? '/' : path);
  const img = image || (page.image ? { url: page.image, width: 1200, height: 630, alt: fullTitle } : DEFAULT_OG_IMAGE);

  const ld = [...schema];
  if (faqs && faqs.length) ld.push(faqSchema(faqs));
  if (page.product) ld.push(breadcrumbSchema([{ name: 'Home', path: '/' }, { name: page.product, path }]));

  return (
    <Helmet htmlAttributes={{ lang: LANG }}>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="robots" content={indexable ? 'index, follow, max-image-preview:large' : 'noindex, follow'} />
      {indexable && <link rel="canonical" href={url} />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={LOCALE} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={absoluteUrl(img.url)} />
      <meta property="og:image:width" content={String(img.width)} />
      <meta property="og:image:height" content={String(img.height)} />
      <meta property="og:image:alt" content={img.alt} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={absoluteUrl(img.url)} />
      {ld.map((s, i) => (
        <script type="application/ld+json" key={`${s['@type']}-${i}`}>{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
};

export default Seo;
